import { DateTime } from 'luxon';
import { SortOrder } from '../records';
import {
  type AnnouncementListApiParams,
  type AnnouncementsPagination,
  type AnnouncementsQueryParams,
  type CursorPaginationMeta,
} from './pagination';
import { AnnouncementStatus, AnnouncementViews } from './types';

export const ANNOUNCEMENTS_DEFAULT_PAGE_LIMIT = 25;

export function mapAnnouncementViewToApiStatus(
  status?: AnnouncementViews | null,
): AnnouncementStatus | undefined {
  switch (status) {
    case AnnouncementViews.LIVE:
    case AnnouncementViews.PAST:
      return AnnouncementStatus.PUBLISHED;
    case AnnouncementViews.DRAFT:
      return AnnouncementStatus.DRAFT;
    default:
      return undefined;
  }
}

export function toAnnouncementListApiParams(
  params: AnnouncementsQueryParams,
  options?: { cursor?: string },
): AnnouncementListApiParams {
  const {
    status,
    page: _page,
    cursor: _cursor,
    pagination: _pagination,
    sortBy: _sortBy,
    sortOrder: _sortOrder,
    ...rest
  } = params;

  const apiStatus = mapAnnouncementViewToApiStatus(status);
  const cursor = options?.cursor;

  const wireParams = { ...rest };
  if (status === AnnouncementViews.DRAFT) {
    delete wireParams.end_date_start;
    delete wireParams.end_date_end;
  }

  return {
    ...wireParams,
    pagination: 'cursor',
    limit: params.limit ?? ANNOUNCEMENTS_DEFAULT_PAGE_LIMIT,
    ...(apiStatus ? { status: apiStatus } : {}),
    ...(cursor ? { cursor } : {}),
  };
}

/**
 * PrimeVue requires a synthetic extra record to enable Next. The UI intentionally
 * presents this as page navigation rather than a real announcement total.
 */
export function buildCursorPaginationMeta({
  pageIndex,
  limit,
  pageLength,
  hasNext,
}: {
  pageIndex: number;
  limit: number;
  pageLength: number;
  hasNext: boolean;
}): CursorPaginationMeta {
  const totalResults = hasNext ? (pageIndex + 1) * limit + 1 : pageIndex * limit + pageLength;

  return {
    resultCount: pageLength,
    totalPages: Math.max(1, Math.ceil(totalResults / limit) || 1),
    totalResults,
  };
}

export const announcementsListPaginationConfig: AnnouncementsPagination = {
  queryParams: {
    status: AnnouncementViews.LIVE,
    end_date_start: DateTime.local().toJSDate().toISOString(),
    end_date_end: DateTime.local().plus({ days: 365 }).endOf('day').toJSDate().toISOString(),
    limit: ANNOUNCEMENTS_DEFAULT_PAGE_LIMIT,
    sortBy: 'end_date_start',
    sortOrder: SortOrder.DESC,
  },
  metaParams: {
    resultCount: 0,
    totalPages: 1,
    totalResults: 0,
  },
};
